import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, RefreshCw, RotateCcw, Edit2, Wrench, Pencil } from "lucide-react";
import BulkRenameQualifiersModal from "@/components/BulkRenameQualifiersModal";
import FixTeamNamesModal from "@/components/FixTeamNamesModal";
// iter87.180.9 — Swap window.confirm for BrutalConfirm on every async
// destructive path (iOS Safari silently drops native confirms).
import { useBrutalConfirm } from "@/components/BrutalConfirm";

export default function TeamsTab({ tournament, league, leagueId, isLocked, onChanged }) {
    const [newTeamName, setNewTeamName] = useState("");
    const [newTeamGroup, setNewTeamGroup] = useState("");
    const [newTeamFlag, setNewTeamFlag] = useState("");
    const [teamRenameDrafts, setTeamRenameDrafts] = useState({});
    const [teamRenameSaving, setTeamRenameSaving] = useState(null);
    const { confirm: brutalConfirm, hostElement: confirmHost } = useBrutalConfirm();
    // iter87.177 — Per-team group re-assign state. Tracks which team
    // is currently editing its group and any in-flight save.
    const [teamGroupSaving, setTeamGroupSaving] = useState(null);
    const [teamGroupDrafts, setTeamGroupDrafts] = useState({});
    const [showNewGroupInput, setShowNewGroupInput] = useState(false);
    const [newGroupPending, setNewGroupPending] = useState("");
    const [bulkRenameOpen, setBulkRenameOpen] = useState(false);
    const [fixTeamNamesOpen, setFixTeamNamesOpen] = useState(false);
    // iter59 — Per-tournament draft-lock check. The legacy global
    // `league.draft_id` would falsely lock teams in tournaments that
    // don't actually have a draft yet (multi-tournament leagues).
    const hasDraft = !!(tournament?.id && league?.drafts_by_tournament?.[tournament.id]);

    const groupedTeams = tournament.teams.reduce((acc, t) => {
        const g = t.group || "Ungrouped";
        if (!acc[g]) acc[g] = [];
        acc[g].push(t);
        return acc;
    }, {});

    // iter87.177 — Distinct group values across the roster, used to
    // populate the per-team bracket dropdown. Includes the special
    // "— No group —" clear option and a "+ New group" trigger.
    const existingGroups = Array.from(
        new Set((tournament.teams || [])
            .map(t => t.group)
            .filter(g => g && g.trim()))
    ).sort();

    // iter87.177 — Build the GotSport team-page URL when both the team
    // has a captured `gotsport_team_id` AND the tournament has a
    // stored `gotsport_event_id`. Falls back to null so components
    // that render team names can decide whether to link.
    const gotsportTeamUrl = (t) => {
        if (!t?.gotsport_team_id || !tournament?.gotsport_event_id) return null;
        return `https://system.gotsport.com/org_event/events/${tournament.gotsport_event_id}/schedules?team=${t.gotsport_team_id}`;
    };

    const handleChangeTeamGroup = async (teamId, group) => {
        setTeamGroupSaving(teamId);
        try {
            await api.updateTeamGroup(leagueId, teamId, group || "");
            toast.success(group ? `Group set to ${group}` : "Group cleared");
            setTeamGroupDrafts(p => { const c = { ...p }; delete c[teamId]; return c; });
            onChanged?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to update group");
        } finally {
            setTeamGroupSaving(null);
        }
    };

    const handleAddTeam = async () => {
        if (!newTeamName.trim()) return;
        try {
            await api.addTeam(tournament.id, {
                name: newTeamName.trim(),
                group: newTeamGroup.trim() || null,
                flag: newTeamFlag.trim() || null,
            });
            toast.success("Team added");
            setNewTeamName(""); setNewTeamGroup(""); setNewTeamFlag("");
            onChanged?.();
        } catch (err) {
            toast.error("Failed");
        }
    };

    const handleRemoveTeam = async (teamId) => {
        if (!(await brutalConfirm({
            title: "Remove team?",
            body: "Existing match history stays but the team disappears.",
            confirmLabel: "Remove",
            destructive: true,
        }))) return;
        await api.removeTeam(tournament.id, teamId);
        onChanged?.();
    };

    const handleApplyFifaTop32 = async () => {
        if (!(await brutalConfirm({
            title: "Reset to bracket placeholders?",
            body: "Resets all 32 teams to the canonical bracket-slot placeholders (1A, 2A, ..., 3L).\n\nUseful if you've edited teams and want to restart from a clean placeholder list. The draft must not have started yet.\n\nESPN auto-rename will fill in real qualifier names from these placeholders as group stage finalises.",
            confirmLabel: "Reset",
            destructive: true,
        }))) return;
        try {
            const r = await api.applyFifaTop32(leagueId);
            toast.success(`Reset ${r.teams_count} teams to bracket placeholders. ESPN will populate real qualifier names as they're confirmed.`);
            onChanged?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to reset placeholders");
        }
    };

    const handleSaveTeamRename = async (teamId, currentName) => {
        const proposed = (teamRenameDrafts[teamId] || "").trim();
        if (!proposed || proposed === currentName) {
            setTeamRenameDrafts(p => { const c = { ...p }; delete c[teamId]; return c; });
            return;
        }
        setTeamRenameSaving(teamId);
        try {
            const r = await api.renameTeam(leagueId, teamId, proposed);
            if (r.renamed === false) {
                toast.info("Name unchanged.");
            } else {
                const reboundMsg = r.scheduled_relinked > 0
                    ? ` (rebound ${r.scheduled_relinked} schedule row${r.scheduled_relinked === 1 ? "" : "s"})`
                    : "";
                toast.success(
                    `Renamed → "${r.new_name}"${reboundMsg}.`
                    + (tournament.api_football_league_id
                        ? " Click Refresh API-Football to re-import past games."
                        : tournament.espn_sport
                        ? " Click Refresh ESPN to re-import past games."
                        : ""),
                );
            }
            setTeamRenameDrafts(p => { const c = { ...p }; delete c[teamId]; return c; });
            onChanged?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Rename failed");
        } finally {
            setTeamRenameSaving(null);
        }
    };

    const handleRefreshEspnFromTeams = async () => {
        try {
            const r = await api.syncEspn(tournament.id);
            toast.success(`ESPN sync complete (${r?.imported_count ?? 0} new, ${r?.skipped_count ?? 0} skipped).`);
            onChanged?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "ESPN sync failed");
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 min-w-0">
                <h2 className="font-display font-extrabold text-xl uppercase break-words">Teams</h2>
                <div className="flex gap-2 flex-wrap min-w-0">
                    {/* iter63h — Show "Refresh ESPN" only when this
                        tournament is actually bound to ESPN (i.e. has
                        `espn_sport` set). API-Football-only tournaments
                        hide it entirely. */}
                    {(["world_cup", "world_cup_knockout", "world_cup_api", "world_cup_ko_api", "march_madness", "football_playoffs"].includes(tournament.template))
                        && tournament.espn_sport && tournament.espn_league && !tournament.api_football_league_id && (
                        <button
                            data-testid="refresh-espn-from-teams-btn"
                            onClick={handleRefreshEspnFromTeams}
                            title="Re-pull from ESPN. Use AFTER renaming a team — reprocesses previously-skipped matches against the new name so the crewmate gets their points."
                            className="brutal-btn-ghost text-xs flex items-center gap-2 border-cyber text-cyber"
                        >
                            <RefreshCw className="w-3.5 h-3.5" />
                            Refresh ESPN
                        </button>
                    )}
                    {tournament.api_football_league_id && (
                        <button
                            data-testid="refresh-apif-from-teams-btn"
                            onClick={async () => {
                                try {
                                    const r = await (await import("@/lib/api")).api.apiFootballRefresh(leagueId, tournament.id);
                                    if (r.updated_scores || r.new_played_matches) {
                                        toast.success(`Updated ${r.updated_scores} score(s); ${r.new_played_matches} newly played.`);
                                    } else {
                                        toast.success("Refreshed — no changes.");
                                    }
                                    onChanged?.();
                                } catch (e) {
                                    toast.error(e.response?.data?.detail || "Refresh failed");
                                }
                            }}
                            title="Re-pull from API-Football. Use AFTER renaming a team so previously-skipped matches re-process."
                            className="brutal-btn-ghost text-xs flex items-center gap-2 border-volt text-volt"
                        >
                            <RefreshCw className="w-3.5 h-3.5" />
                            Refresh API-Football
                        </button>
                    )}
                    <button
                        data-testid="fix-team-names-btn"
                        onClick={() => setFixTeamNamesOpen(true)}
                        disabled={isLocked}
                        title="Bulk modal: shows unmatched ESPN names + one-click suggestions. Use for fixing many teams at once."
                        className="brutal-btn-ghost text-xs flex items-center gap-2 border-blaze text-blaze disabled:opacity-40"
                    >
                        <Wrench className="w-3.5 h-3.5" />
                        Bulk fix
                    </button>
                    {tournament.template === "world_cup_knockout" && (
                        <>
                            <button
                                data-testid="bulk-rename-qualifiers-btn"
                                onClick={() => setBulkRenameOpen(true)}
                                disabled={isLocked || hasDraft}
                                title={hasDraft ? "Clear the draft first to rename" : "Edit all 32 qualifier names at once"}
                                className="brutal-btn-ghost text-xs flex items-center gap-2 border-neon text-neon disabled:opacity-40"
                            >
                                <Edit2 className="w-3.5 h-3.5" />
                                Bulk rename qualifiers
                            </button>
                            <button
                                data-testid="apply-fifa-top32-btn"
                                onClick={handleApplyFifaTop32}
                                disabled={isLocked || hasDraft}
                                title={hasDraft ? "Clear the draft first to reseed" : "Reset all 32 teams to bracket-slot placeholders (1A, 2B, ...)"}
                                className="brutal-btn-ghost text-xs flex items-center gap-2 border-cyber text-cyber disabled:opacity-40"
                            >
                                <RotateCcw className="w-3.5 h-3.5" />
                                Reset to bracket placeholders
                            </button>
                        </>
                    )}
                </div>
            </div>
            <p className="text-[11px] text-zinc-500 leading-relaxed">
                <span className="text-cyber font-bold">Tip:</span> click a team name to edit it inline. Rename keeps the same <em>team ID</em> — your players' drafts, scores, and bonus picks stay intact.
                {tournament.api_football_league_id ? (
                    <> After a rename, hit <span className="text-volt font-bold">Refresh API-Football</span> so any previously-skipped matches re-process and points flow.</>
                ) : (["world_cup", "world_cup_knockout", "world_cup_api", "world_cup_ko_api", "march_madness", "football_playoffs"].includes(tournament.template) && tournament.espn_sport) ? (
                    <> After a rename, hit <span className="text-cyber font-bold">Refresh ESPN</span> so any previously-skipped matches re-process and points flow.</>
                ) : null}
            </p>
            {tournament.template === "world_cup_knockout" && (
                <p className="text-[11px] text-zinc-500 -mt-2" data-testid="fifa-top32-helper-copy">
                    The 32 teams below are <span className="text-cyber font-bold">bracket-slot placeholders</span> (1A = Group A winner, 2A = Group A runner-up, 3A = best 3rd-place team facing Group A's winner, etc.). <span className="text-neon font-bold">ESPN auto-rename</span> fills in real qualifier names as group stage finalises — no manual action needed for the happy path. Use <span className="text-cyber font-bold">Reset to bracket placeholders</span> only if you've edited rows and want to start over (pre-draft only).
                </p>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 min-w-0">
                <input data-testid="new-team-name-input" placeholder="Team name" value={newTeamName} onChange={(e) => setNewTeamName(e.target.value)} className="brutal-input sm:col-span-2 min-w-0" />
                <input data-testid="new-team-group-input" placeholder="Group" value={newTeamGroup} onChange={(e) => setNewTeamGroup(e.target.value)} className="brutal-input" />
                <input data-testid="new-team-flag-input" placeholder="🇯🇵" value={newTeamFlag} onChange={(e) => setNewTeamFlag(e.target.value)} className="brutal-input" />
                <button data-testid="add-team-btn" onClick={handleAddTeam} disabled={isLocked} className="brutal-btn-primary disabled:opacity-40">
                    <Plus className="w-4 h-4" /> Add
                </button>
            </div>

            {/* iter87.177 — "+ New group" inline prompt. Triggered when
                the admin picks the "+ New group…" option in any team's
                bracket dropdown. Whatever they type becomes the new
                group value AND is saved to the team whose dropdown
                triggered it. */}
            {showNewGroupInput && (
                <div className="brutal-card p-3 border-cyber/40 space-y-2" data-testid="new-group-inline-card">
                    <p className="text-[11px] text-cyber font-bold uppercase tracking-widest">New group name</p>
                    <div className="flex items-center gap-2">
                        <input
                            data-testid="new-group-name-input"
                            autoFocus
                            placeholder="e.g. Bracket C"
                            value={newGroupPending}
                            onChange={(e) => setNewGroupPending(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && newGroupPending.trim()) {
                                    // Save to whichever team currently has an
                                    // in-flight draft with __NEW__ intent.
                                    const editingTeamId = Object.keys(teamGroupDrafts)[0];
                                    if (editingTeamId) {
                                        handleChangeTeamGroup(editingTeamId, newGroupPending.trim());
                                    }
                                    setShowNewGroupInput(false);
                                    setNewGroupPending("");
                                }
                                if (e.key === "Escape") {
                                    setShowNewGroupInput(false);
                                    setNewGroupPending("");
                                    setTeamGroupDrafts({});
                                }
                            }}
                            className="brutal-input flex-1 text-sm min-w-0"
                        />
                        <button
                            data-testid="new-group-save-btn"
                            disabled={!newGroupPending.trim()}
                            onClick={() => {
                                const editingTeamId = Object.keys(teamGroupDrafts)[0];
                                if (editingTeamId) {
                                    handleChangeTeamGroup(editingTeamId, newGroupPending.trim());
                                }
                                setShowNewGroupInput(false);
                                setNewGroupPending("");
                            }}
                            className="brutal-btn-primary text-xs disabled:opacity-30 shrink-0"
                        >
                            Save
                        </button>
                        <button
                            data-testid="new-group-cancel-btn"
                            onClick={() => {
                                setShowNewGroupInput(false);
                                setNewGroupPending("");
                                setTeamGroupDrafts({});
                            }}
                            className="text-zinc-500 hover:text-white text-xs shrink-0"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {Object.entries(groupedTeams).map(([group, teams]) => (
                <div key={group}>
                    <p className="text-xs uppercase tracking-widest text-zinc-500 mb-2 mt-3">Group {group}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 min-w-0">
                        {teams.map(t => {
                            const draftVal = teamRenameDrafts[t.id];
                            const isEditing = draftVal !== undefined;
                            const showVal = isEditing ? draftVal : t.name;
                            const hasChange = isEditing && draftVal.trim() !== "" && draftVal.trim() !== t.name;
                            return (
                                <div key={t.id} data-testid={`team-row-${t.id}`} className={`flex items-center justify-between gap-2 p-3 border-2 rounded-md bg-ink min-w-0 ${isEditing ? "border-cyber" : "border-[#333]"}`}>
                                    <div className="flex items-center gap-2 min-w-0 flex-1">
                                        <span className="text-xl shrink-0">{t.flag || "🏳️"}</span>
                                        <div className="min-w-0 flex-1">
                                            {isEditing ? (
                                                <div className="flex items-center gap-1">
                                                    <input
                                                        data-testid={`team-name-input-${t.id}`}
                                                        type="text"
                                                        value={showVal}
                                                        placeholder={t.name}
                                                        onChange={e => setTeamRenameDrafts(p => ({ ...p, [t.id]: e.target.value }))}
                                                        onKeyDown={e => {
                                                            if (e.key === "Enter") handleSaveTeamRename(t.id, t.name);
                                                            if (e.key === "Escape") setTeamRenameDrafts(p => { const c = { ...p }; delete c[t.id]; return c; });
                                                        }}
                                                        className="brutal-input text-sm py-1 flex-1 min-w-0"
                                                        autoFocus
                                                    />
                                                    <button
                                                        data-testid={`team-name-save-${t.id}`}
                                                        onClick={() => handleSaveTeamRename(t.id, t.name)}
                                                        disabled={!hasChange || teamRenameSaving === t.id}
                                                        className="brutal-btn-primary text-[10px] px-2 py-1 shrink-0 disabled:opacity-30"
                                                    >
                                                        {teamRenameSaving === t.id ? "…" : "Save"}
                                                    </button>
                                                    <button
                                                        data-testid={`team-name-cancel-${t.id}`}
                                                        onClick={() => setTeamRenameDrafts(p => { const c = { ...p }; delete c[t.id]; return c; })}
                                                        disabled={teamRenameSaving === t.id}
                                                        className="text-zinc-500 hover:text-white px-1 shrink-0"
                                                        title="Cancel"
                                                    >
                                                        ✕
                                                    </button>
                                                </div>
                                            ) : (
                                                (() => {
                                                    const gsUrl = gotsportTeamUrl(t);
                                                    const nameNode = (
                                                        <button
                                                            data-testid={`team-name-edit-${t.id}`}
                                                            onClick={() => setTeamRenameDrafts(p => ({ ...p, [t.id]: t.name }))}
                                                            disabled={isLocked}
                                                            title="Click to rename. Preserves team ID — no redraft needed."
                                                            className="font-bold truncate text-left hover:text-cyber transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-1 w-full max-w-full min-w-0"
                                                        >
                                                            <span className="truncate min-w-0">{t.name}</span>
                                                            <Pencil className="w-2.5 h-2.5 text-zinc-600 shrink-0" />
                                                        </button>
                                                    );
                                                    if (!gsUrl) return nameNode;
                                                    // iter87.177 — Ghost external link to the GotSport
                                                    // team page. Sits next to the name so the click
                                                    // surface for rename stays large + obvious.
                                                    return (
                                                        <div className="flex items-center gap-1 min-w-0">
                                                            {nameNode}
                                                            <a
                                                                data-testid={`team-gotsport-link-${t.id}`}
                                                                href={gsUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-cyber hover:text-cyber-glow text-[10px] font-bold shrink-0"
                                                                title="Open on GotSport"
                                                            >
                                                                GS↗
                                                            </a>
                                                        </div>
                                                    );
                                                })()
                                            )}
                                            {tournament.template === "world_cup_knockout" ? (
                                                <p className="text-[10px] text-zinc-500 mt-0.5">
                                                    <span className="font-mono">{t.bracket_slot || "—"}</span>
                                                    {" · "}{t.stage_reached}
                                                    {t.eliminated && " · 💔"}
                                                </p>
                                            ) : (
                                                <p className="text-[10px] text-zinc-500 mt-0.5">
                                                    {t.group_wins}W-{t.group_draws}D-{t.group_losses}L · {t.stage_reached}
                                                    {t.eliminated && " · 💔"}
                                                </p>
                                            )}
                                            {/* iter87.177 — Bracket / group selector. Dropdown
                                                shows existing brackets + a "+ New" trigger.
                                                Hidden for knockout templates that use
                                                bracket_slot instead. */}
                                            {tournament.template !== "world_cup_knockout" && !isEditing && (
                                                <div className="mt-1 flex items-center gap-1 min-w-0" data-testid={`team-group-row-${t.id}`}>
                                                    <span className="text-[9px] uppercase tracking-wider text-zinc-600 shrink-0">Group:</span>
                                                    <select
                                                        data-testid={`team-group-select-${t.id}`}
                                                        value={teamGroupDrafts[t.id] !== undefined ? teamGroupDrafts[t.id] : (t.group || "")}
                                                        disabled={isLocked || teamGroupSaving === t.id}
                                                        onChange={(e) => {
                                                            const v = e.target.value;
                                                            if (v === "__NEW__") {
                                                                setShowNewGroupInput(true);
                                                                setTeamGroupDrafts(p => ({ ...p, [t.id]: t.group || "" }));
                                                                return;
                                                            }
                                                            handleChangeTeamGroup(t.id, v);
                                                        }}
                                                        className="brutal-input text-[10px] py-0.5 px-1 min-w-0 flex-1"
                                                    >
                                                        <option value="">— No group —</option>
                                                        {existingGroups.map(g => (
                                                            <option key={g} value={g}>{g}</option>
                                                        ))}
                                                        <option value="__NEW__">+ New group…</option>
                                                    </select>
                                                    {t.group_manually_set && (
                                                        <span
                                                            className="text-[9px] text-volt shrink-0"
                                                            title="Manually assigned — auto-sync won't overwrite"
                                                        >
                                                            ✎
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    {!isEditing && (
                                        <button data-testid={`remove-team-${t.id}-btn`} onClick={() => handleRemoveTeam(t.id)} className="brutal-btn-ghost p-2 shadow-none border-[#333] shrink-0">
                                            <Trash2 className="w-4 h-4 text-heat" />
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            ))}

            {tournament.template === "world_cup_knockout" && (
                <BulkRenameQualifiersModal
                    open={bulkRenameOpen}
                    onOpenChange={setBulkRenameOpen}
                    leagueId={leagueId}
                    teams={tournament.teams || []}
                    onSaved={onChanged}
                />
            )}
            <FixTeamNamesModal
                open={fixTeamNamesOpen}
                onOpenChange={setFixTeamNamesOpen}
                leagueId={leagueId}
                tournament={tournament}
                teams={tournament.teams || []}
                onSaved={onChanged}
            />
            {confirmHost}
        </div>
    );
}
